package com.routexk.quiz.funnel.ui

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.Composable
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.toRgbString
import com.routexk.quiz.utils.PageSeo
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.*

/**
 * Funnel page wrapper: SEO title, page background (cool gray or the warm
 * "reveal" gradient), centered logo, centered content column.
 */
@Composable
fun FunnelLayout(
    title: String,
    description: String = "",
    warm: Boolean = false,
    maxContentWidth: com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue = 640.rpx,
    /** Stretch the content column to the full viewport height so pages can pin their CTA to the bottom. */
    fillViewport: Boolean = false,
    /** Horizontal center of the warm background ball, in viewport %. The results design shifts it left of center. */
    patternLeftPercent: Int = 50,
    /** Top offset of the warm background ball; negative values lift it above the viewport edge. */
    patternTop: CSSSizeValue<CSSUnit.rem> = 120.rpx,
    /** Some screens (e.g. desktop paywall) start straight with content, without the logo. */
    showLogo: Boolean = true,
    content: @Composable () -> Unit
) {
    PageSeo(title = title, description = description)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            // Use the *dynamic* viewport height on mobile: plain 100vh includes
            // the area behind the browser toolbar, which pushes the bottom CTA
            // below the fold and forces a scroll. 100dvh = the truly visible area.
            .styleModifier {
                property("min-height", "100vh")
                property("min-height", "100dvh")
            }
            .backgroundColor(AppColors.Funnel.PageBackground)
            .position(Position.Relative)
            // Clip only horizontally (decorative ball + 100vw CTA bar bleed past
            // the edges). A plain overflow:hidden also clipped vertically, which
            // cut off the bottom CTA on short/zoomed phone viewports with no way
            // to scroll to it. "clip" keeps overflow-y visible; "hidden" is the
            // fallback for older browsers.
            .styleModifier {
                property("overflow-x", "hidden")
                property("overflow-x", "clip")
            }
            // Small bottom padding: 24px was enough to push pages that otherwise
            // fit (e.g. results on tall phones) a few px over, causing a phantom scroll.
            .padding(top = 12.rpx, bottom = 12.rpx, leftRight = 16.rpx),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (warm) {
            // Blurred decorative ball from the design (opacity .2, blur 30px). On
            // mobile it matches the mobile frame's larger blob (1052x837) so the
            // warm glow sits behind the cards, exactly as in Figma. The wrapper
            // clips it on all sides (like the Figma frame does) — the ball is
            // taller than the viewport and would otherwise add a vertical
            // scrollbar, since the page itself only clips horizontally.
            val ballMobile = com.routexk.quiz.ui.utils.isMobile()
            org.jetbrains.compose.web.dom.Div({
                attr("aria-hidden", "true")
                style {
                    position(Position.Absolute)
                    top(0.px)
                    left(0.px)
                    right(0.px)
                    bottom(0.px)
                    overflow("hidden")
                    property("pointer-events", "none")
                }
            }) {
                org.jetbrains.compose.web.dom.Img(
                    src = "/funnel/hero-pattern-ball.svg",
                    attrs = {
                        style {
                            position(Position.Absolute)
                            left(patternLeftPercent.percent)
                            top(patternTop)
                            width((if (ballMobile) 1052 else 1000).rpx)
                            height((if (ballMobile) 837 else 796).rpx)
                            property("transform", "translateX(-50%)")
                            opacity(if (ballMobile) 0.28 else 0.2)
                            property("filter", "blur(30px)")
                        }
                    }
                )
            }
        }
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(maxContentWidth)
                .position(Position.Relative)
                .zIndex(1)
                .then(if (fillViewport) Modifier.flexGrow(1) else Modifier),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            if (showLogo) {
                FunnelLogo(Modifier.margin(bottom = 18.rpx).alignSelf(com.varabyte.kobweb.compose.css.AlignSelf.Center), height = 20)
            }
            content()
        }
    }
}
